import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import Home from './components/Home';
import Productos from './components/Products';
import OtherProducts from './components/OtherProducts';
import Navbar from './components/Navbar';
import Login from './components/Login';
import Cart from './components/Cart';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import AdminPanel from './components/AdminPanel';
import OrderStats from './components/OrderStats';
import Company from './components/Company';
import SubscriberList from './components/SubscriberList';
import MassEditor from './components/MassEditor';
import OrderList from './components/OrderList';
import DiscountConfig from './components/DiscountConfig';
import LocalSales from './components/LocalSales';
import StockManagement from './components/StockManagement'; 
import SuccessPage from './components/SuccessPage'; 
import QRScanner from './components/QRScanner';
import QRCodePage from './components/QRCodePage';
import WallStreetChart from './components/WallStreetChart';
import FreeTicketCreator from './components/FreeTicketCreator';
import QRCodeComponent from './components/QRCodeComponent';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import QRList from './components/QRList';
import AnimatedBackground from './components/AnimatedBackground';
import AdminCustomersPage from './components/AdminCustomersPage';
import AdminStaffUsersPage from './components/AdminStaffUsersPage';
import AdminEventsPage from './components/AdminEventsPage';
import AdminSuppliersPage from './components/AdminSuppliersPage';
import { canAccessAdminShell, getEffectiveStaffFeatureIds } from './config/staffFeatures';
import { useEditMode } from './contexts/EditModeContext';
import { initStaffSessionWatch } from './utils/staffSessionGuard';

import './App.css';

const App = () => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  const { isAdmin, userRole, allowedFeatures, hasFeature } = useEditMode();
  const effectiveFeatures = getEffectiveStaffFeatureIds(userRole, allowedFeatures);
  const canOpenAdminPanel = canAccessAdminShell(isAdmin, userRole, allowedFeatures);
  const canAccessFeature = (featureId) => isAdmin || hasFeature(featureId) || effectiveFeatures.includes(featureId);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Vigilancia de sesión del panel: observa users/{uid}.authDisabled,
  // refresca el token periódicamente y fuerza logout ante sesión inválida.
  useEffect(() => {
    return initStaffSessionWatch();
  }, []);

  const removeFromCart = (id, collectionName) => {
    setCart(cart.filter(item => !(item.id === id && (!collectionName || item.collection === collectionName))));
  };

  const clearCart = () => {
    setCart([]);
  };

  const updateCartItemQuantity = (itemId, newQuantity, collectionName) => {
    setCart(cart.map(item => (
      item.id === itemId && (!collectionName || item.collection === collectionName)
        ? { ...item, quantity: newQuantity }
        : item
    )));
  };

  const recaptchaFrontend = process.env.REACT_APP_RECAPTCHA_FRONTEND;
  const websiteURL = 'https://alegra-club.web.app'; // Reemplaza con tu URL

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaFrontend}>
      <Router>
        <div className="App">
          <AnimatedBackground />
          <Navbar cart={cart} />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/nosotros" element={<Company />} />
            <Route path="/contacto" element={<ContactForm />} />
            <Route path="/entradas" element={<Productos cart={cart} setCart={setCart} />} />
            <Route path="/consumo-interno" element={<OtherProducts cart={cart} setCart={setCart} />} />
            <Route path="/carrito" element={<Cart cart={cart} setCart={setCart} removeFromCart={removeFromCart} clearCart={clearCart} updateCartItemQuantity={updateCartItemQuantity} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/success" element={<SuccessPage />} /> 
            <Route path="/qr/:qrValue" element={<QRCodePage />} />
            {canOpenAdminPanel && <Route path="/admin" element={<AdminPanel />} />}
            {canAccessFeature('subscribers') && <Route path="/subscriptores" element={<SubscriberList />} />}
            {canAccessFeature('massEditor') && <Route path="/editor-masivo" element={<MassEditor />} />}
            {canAccessFeature('salesHistory') && <Route path="/historial-ventas" element={<OrderList />} />}
            {canAccessFeature('salesMetrics') && <Route path="/metricas" element={<OrderStats />} />}
            {canAccessFeature('salesChart') && <Route path="/grafico" element={<WallStreetChart />} />}
            {canAccessFeature('freeTickets') && <Route path="/free" element={<FreeTicketCreator />} />}
            {canAccessFeature('qrScanner') && <Route path="/escanear-qr" element={<QRScanner userRole={userRole} />} />}
            {canAccessFeature('localSales') && <Route path="/local" element={<LocalSales />} />}
            {canAccessFeature('discountConfig') && <Route path="/configuracion-descuentos" element={<DiscountConfig />} />}
            {canAccessFeature('stockManagement') && <Route path="/gestion-stock" element={<StockManagement />} />} 
            {canAccessFeature('qrList') && <Route path="/qr-lista" element={<QRList />} />} 
            {canAccessFeature('adminEvents') && <Route path="/admin/eventos" element={<AdminEventsPage />} />}
            {canAccessFeature('suppliers') && <Route path="/admin/proveedores" element={<AdminSuppliersPage />} />}
            {canAccessFeature('adminCustomers') && (
              <Route path="/admin/clientes" element={<AdminCustomersPage />} />
            )}
            {isAdmin && <Route path="/admin/usuarios-staff" element={<AdminStaffUsersPage />} />}
            <Route path="/qr" element={<QRCodeComponent url={websiteURL} />} /> 
          </Routes>
          <Footer />
        </div>
      </Router>
    </GoogleReCaptchaProvider>
  );
};

export default App;





